import { markRaw, reactive, ref } from 'vue';

// The declarative data loader every rdrive dashboard shares (PLATFORM.md §6 decision 24). An app
// describes its tables once, in a DATA MANIFEST (where each table comes from, in which format,
// whether it loads at boot or on demand, and the DDL around it), and this turns that into one
// load lifecycle with progress the welcome dialog can render. It replaces the inline
// `apiRequests` array, the hand-counted request total and the ad-hoc lazy loads an app's boot
// used to carry. This file holds ZERO project SQL and no table names (PLATFORM.md §3.2 rules
// 1–2): every name, path and statement arrives in the manifest, which is app code (for this
// repo: `apps/project-overview/src/data/manifest.js`).
//
// It owns no transport and no database: the app's ONE DuckDBService instance is passed in, and
// every fetch goes through that service's existing loaders (and so through its proxy fetcher —
// the request on the wire is whatever DuckDBService sends for (path, table)).
//
// ---------------------------------------------------------------------------
// Contract (README.md has the full field table)
// ---------------------------------------------------------------------------
//   createDataLoader({ duckdb, manifest, params?, hooks? }) → {
//     load()                     the boot load: every non-lazy table, then the hooks
//     ensure(name)               a lazy table, loaded once (concurrent callers share the load)
//     reload(name?, { append, params })   re-fetch one table; no name = load()
//     progress                   reactive { total, done, loaded, failed, tables }
//     dataLoaded                 ref — true once the boot load and hooks.onLoaded are done
//   }
//   manifest = { tables: TableEntry[], ddl?: { pre?, post? } }
//
// load() lifecycle, in this order:
//   1. dataLoaded = false; DuckDB's query gate closed (`duckdb.isDataLoaded = false`)
//   2. ddl.pre
//   3. every boot (non-lazy) table in parallel, each followed by its own postLoad
//   4. ddl.post
//   5. the query gate opened (`duckdb.isDataLoaded = true`)
//   6. hooks.onLoaded
//   7. dataLoaded = true
// Steps 5–7 are the point of the package: onLoaded (lookups, filter-context init) runs with
// `conn.query` already answering, and FINISHES before dataLoaded flips — so no page's first
// query can reach a view or lookup that onLoaded has not built yet (project-overview's R2 boot
// race: its charts used to query a filter-context view before the context had created it).

/**
 * @typedef {Object} TableEntry
 * @property {string} name                       DuckDB table name (unique in the manifest)
 * @property {string | ((params: Object) => string)} path  API path; a string's `{key}`
 *   placeholders are filled from the params, raw (no encoding — the proxy URL builder
 *   normalises the URL). It may carry its own `?query`.
 * @property {Object | ((params: Object) => Object)} [query]  extra query params, appended to
 *   the PATH (so they land before `url`/`scopedToken` on the proxy URL, exactly where a literal
 *   `?query` in the path lands). Values are strings (placeholders filled), numbers or arrays
 *   (one `key=value` per item).
 * @property {'parquet' | 'arrow' | 'json'} format  picks the DuckDBService loader
 * @property {Object<string, string>} [headers]  extra request headers for this table's fetch,
 *   passed to the DuckDBService loader's `headers` option (merged over its own Accept; none
 *   given = the loader's request exactly as before — e.g. issue-analysis's JSON tables send
 *   `Accept: application/json`, project-overview's send no Accept at all)
 * @property {boolean} [paged=false]             parquet only: the page-until-empty loader
 * @property {boolean} [lazy=false]              not loaded at boot; `loader.ensure(name)`
 * @property {boolean} [optional=false]          a failure is a warning, not an error
 * @property {string} [labelKey]                 i18n key for a loading UI; `tables.<name>`
 * @property {string | string[] | ((context: HookContext) => any)} [postLoad]  runs right
 *   after this table lands (SQL strings through `conn.loadData`, in order)
 */

/**
 * @typedef {Object} Manifest
 * @property {TableEntry[]} tables
 * @property {{ pre?: string | string[] | Function, post?: string | string[] | Function }} [ddl]
 */

/**
 * @typedef {Object} HookContext
 * @property {Object} conn     `duckdb.conn` — `query` (gated), `queryRaw`, `loadData`
 * @property {Object} duckdb   the DuckDBService instance
 * @property {Object} params   the params this load read
 */

const FORMATS = ['parquet', 'arrow', 'json'];

const unknownTable = (name) => new Error(`@rdrive/data-loader: no table "${name}" in the manifest`);

// Fill `{key}` placeholders from the params. A missing (or empty) param is an error for this
// table rather than a request to `/api/undefined/…`.
const interpolate = (template, params, name) =>
  template.replace(/\{(\w+)\}/g, (_, key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') {
      throw new Error(`@rdrive/data-loader: table "${name}" needs the "${key}" param`);
    }
    return String(value);
  });

// The path as DuckDBService receives it. `query` is folded INTO the path, never passed as the
// fetcher's params: those land after `url`/`scopedToken`, and the endpoints that take a query
// (issue-analysis's `templateTags`, `fieldTags`) have always had it before them.
const resolvePath = (entry, params) => {
  const path = typeof entry.path === 'function' ? entry.path(params) : interpolate(entry.path, params, entry.name);
  if (!entry.query) return path;

  const query = typeof entry.query === 'function' ? entry.query(params) : entry.query;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query || {})) {
    for (const item of [].concat(value)) {
      if (item === undefined || item === null) continue;
      search.append(key, typeof item === 'string' ? interpolate(item, params, entry.name) : String(item));
    }
  }
  const qs = search.toString();
  if (!qs) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${qs}`;
};

// A hook step: a function, or SQL run one statement at a time through `conn.loadData` (which
// logs a failed statement and returns false — the way the app's boot DDL always ran).
const runStep = async (step, context) => {
  if (!step) return;
  if (typeof step === 'function') {
    await step(context);
    return;
  }
  for (const sql of [].concat(step)) {
    await context.conn.loadData(sql);
  }
};

const validate = (manifest) => {
  if (!manifest || !Array.isArray(manifest.tables)) {
    throw new Error('@rdrive/data-loader: the manifest needs a `tables` array');
  }
  const seen = new Set();
  for (const entry of manifest.tables) {
    if (!entry || typeof entry.name !== 'string' || !entry.name) {
      throw new Error('@rdrive/data-loader: every table needs a `name`');
    }
    if (seen.has(entry.name)) throw new Error(`@rdrive/data-loader: table "${entry.name}" is listed twice`);
    seen.add(entry.name);
    if (typeof entry.path !== 'string' && typeof entry.path !== 'function') {
      throw new Error(`@rdrive/data-loader: table "${entry.name}" needs a \`path\``);
    }
    if (!FORMATS.includes(entry.format)) {
      throw new Error(`@rdrive/data-loader: table "${entry.name}" has format "${entry.format}" (expected ${FORMATS.join(' | ')})`);
    }
    if (entry.paged && entry.format !== 'parquet') {
      throw new Error(`@rdrive/data-loader: table "${entry.name}" is paged, which is parquet-only`);
    }
    if (entry.headers !== undefined && (!entry.headers || typeof entry.headers !== 'object' || Array.isArray(entry.headers))) {
      throw new Error(`@rdrive/data-loader: table "${entry.name}" has \`headers\` that are not an object`);
    }
  }
};

/**
 * @param {Object} options
 * @param {Object} options.duckdb       the app's DuckDBService instance (its four loaders,
 *   `conn`, and the `isDataLoaded` query gate)
 * @param {Manifest} options.manifest
 * @param {Object | (() => Object)} [options.params]  platform params for the placeholders; a
 *   getter is read at every load, so a param set after creation (from the URL) is picked up
 * @param {Object} [options.hooks]
 * @param {(context: HookContext) => any} [options.hooks.onLoaded]  after the boot tables and
 *   `ddl.post`, with the query gate open, BEFORE `dataLoaded` flips
 * @param {(event: Object) => void} [options.hooks.onProgress]  every DuckDBService progress
 *   event (`{ name, tableName, status, … }` — the paged loader reports each page)
 */
export function createDataLoader({ duckdb, manifest, params = {}, hooks = {} }) {
  if (!duckdb) throw new Error('@rdrive/data-loader: pass the app\'s DuckDBService as `duckdb`');
  validate(manifest);

  const entries = new Map(manifest.tables.map((entry) => [entry.name, entry]));
  const bootEntries = manifest.tables.filter((entry) => !entry.lazy);
  const ddl = manifest.ddl || {};
  const readParams = () => (typeof params === 'function' ? params() : params) || {};
  const labelKeyOf = (entry) => entry.labelKey || `tables.${entry.name}`;
  const hookContext = (p) => ({ conn: duckdb.conn, duckdb, params: p });

  const dataLoaded = ref(false);

  // `done` counts every boot table that SETTLED, loaded or failed — the number a progress bar
  // shows. It is what the app's hand-counted `apiProgress` always counted: DuckDBService's
  // loaders return false instead of throwing, so the old `apiProgress += 1` after the await ran
  // for failures too, and the welcome dialog reached 13/13 either way.
  const progress = reactive({
    total: bootEntries.length,
    done: 0,
    loaded: 0,
    failed: 0,
    tables: Object.fromEntries(
      manifest.tables.map((entry) => [entry.name, { status: 'idle', labelKey: labelKeyOf(entry), error: null }]),
    ),
  });

  // name → the promise of that table's LATEST load. Kept while it is in flight or once it has
  // succeeded (so ensure() is a no-op); dropped when it fails, so the next ensure() retries.
  const memo = new Map();

  const track = (name, run) => {
    const promise = run().then((ok) => {
      if (!ok && memo.get(name) === promise) memo.delete(name);
      return ok;
    });
    memo.set(name, promise);
    return promise;
  };

  // Failures are reported here because DuckDBService's own error lines go through DuckDB's
  // ConsoleLogger, which drops plain strings — without this a failed table is silent.
  const reportFailures = (failures) => {
    const required = failures.filter(({ entry }) => !entry.optional);
    const optional = failures.filter(({ entry }) => entry.optional);
    const list = (rows) => rows.map(({ entry, error }) => ({ table: entry.name, error }));
    if (required.length > 0) console.error('Some API requests failed:', list(required));
    if (optional.length > 0) console.warn('Some optional API requests failed:', list(optional));
  };

  // Mirrors DuckDBService's progressCallback into progress.tables and the onProgress hook.
  const forwardProgress = (name) => (event) => {
    const { tableName, status, ...detail } = event;
    Object.assign(progress.tables[name], detail);
    if (hooks.onProgress) hooks.onProgress({ name, tableName, status, ...detail });
  };

  const insert = (entry, path, options) => {
    switch (entry.format) {
      case 'parquet':
        return entry.paged
          ? duckdb.downloadAndInsertParquetWithPaging(path, entry.name, options)
          : duckdb.downloadAndInsertParquet(path, entry.name, options);
      case 'arrow':
        return duckdb.downloadAndInsertArrow(path, entry.name, options);
      default: // 'json' — validate() admits nothing else
        return duckdb.downloadAndInsertJSON(path, entry.name, options);
    }
  };

  /**
   * Fetch one table, then run its postLoad. Resolves true/false, never rejects.
   * `replace` drops the table first: every DuckDBService loader creates with
   * `CREATE TABLE IF NOT EXISTS`, so without the drop a re-fetch would leave the old rows.
   * The drop goes through `queryRaw`, not `loadData`: a failed `loadData` shuts the query
   * gate for the whole app, a failed `queryRaw` only throws (and fails this table).
   */
  const loadEntry = async (entry, p, { append = false, replace = false, report = true } = {}) => {
    progress.tables[entry.name] = { status: 'loading', labelKey: labelKeyOf(entry), error: null };
    const options = { progressCallback: forwardProgress(entry.name), throwOnError: true };
    if (entry.format === 'json') options.append = append;
    // Only when the manifest names some: without them the loader's request is unchanged.
    if (entry.headers) options.headers = entry.headers;

    try {
      const path = resolvePath(entry, p);
      if (replace) await duckdb.conn.queryRaw(`DROP TABLE IF EXISTS ${entry.name}`);
      // throwOnError makes a failed fetch/insert throw (with `.status` for HTTP errors); the
      // loaders still return false for "not initialised" and for an empty first parquet page
      if ((await insert(entry, path, options)) === false) {
        throw new Error('the DuckDBService loader returned false (not initialised, or no data)');
      }
      await runStep(entry.postLoad, hookContext(p));
      progress.tables[entry.name].status = 'loaded';
      return true;
    } catch (error) {
      Object.assign(progress.tables[entry.name], { status: 'failed', error: error?.message ?? String(error) });
      if (report) reportFailures([{ entry, error: error?.message ?? String(error) }]);
      return false;
    }
  };

  const boot = async () => {
    // Synchronous up to the first await, like the inline boot it replaces: the flag and the
    // gate are down before load() returns.
    dataLoaded.value = false;
    duckdb.isDataLoaded = false;
    Object.assign(progress, { total: bootEntries.length, done: 0, loaded: 0, failed: 0 });

    const p = readParams();
    const context = hookContext(p);

    try {
      await runStep(ddl.pre, context);
    } catch (error) {
      console.error('@rdrive/data-loader: ddl.pre failed:', error);
    }

    // Every boot table is fetched again on every load(), a previous success or not (the inline
    // boot always re-requested all of them). Lazy tables are left alone — see ensure().
    const results = await Promise.all(
      bootEntries.map((entry) =>
        track(entry.name, () => loadEntry(entry, p, { report: false })).then((ok) => {
          progress.done += 1;
          if (ok) progress.loaded += 1;
          else progress.failed += 1;
          return ok;
        }),
      ),
    );
    const failed = bootEntries.filter((_, i) => !results[i]);
    reportFailures(failed.map((entry) => ({ entry, error: progress.tables[entry.name].error })));

    try {
      await runStep(ddl.post, context);
    } catch (error) {
      console.error('@rdrive/data-loader: ddl.post failed:', error);
    }

    // Open the query gate BEFORE onLoaded: its lookups and filter-context init go through
    // `conn.query`, which answers with an empty result while the gate is shut.
    duckdb.isDataLoaded = true;
    try {
      if (hooks.onLoaded) await hooks.onLoaded(context);
    } finally {
      // Flipped even when onLoaded throws (the data IS loaded); load() then rejects with
      // onLoaded's error, so the caller still sees it.
      dataLoaded.value = true;
    }

    return {
      loaded: bootEntries.filter((_, i) => results[i]).map((entry) => entry.name),
      failed: failed.map((entry) => entry.name),
    };
  };

  // One boot at a time: a load() while one is in flight joins it instead of starting a second
  // wave of the same requests.
  let booting = null;
  const load = () => {
    if (!booting) {
      booting = boot().finally(() => {
        booting = null;
      });
    }
    return booting;
  };

  // A table loaded once (lazy or not): returns the in-flight or settled load if there is one.
  // A full load() does NOT reset a lazy table — it stays in DuckDB and ensure() stays a no-op,
  // which is what the app's `tableExists` check used to give it across a reload.
  const ensure = (name) => {
    if (memo.has(name)) return memo.get(name);
    const entry = entries.get(name);
    if (!entry) return Promise.reject(unknownTable(name));
    return track(name, () => loadEntry(entry, readParams()));
  };

  // Re-fetch ONE table: dropped and replaced, or with `append` (JSON only — the only
  // DuckDBService loader with an append mode) added to. `params` are merged over the loader's
  // for this call only (e.g. a search term in the path). Leaves dataLoaded and the gate alone.
  const reload = (name, { append = false, params: extra } = {}) => {
    if (name === undefined || name === null) return load();
    const entry = entries.get(name);
    if (!entry) return Promise.reject(unknownTable(name));
    if (append && entry.format !== 'json') {
      return Promise.reject(
        new Error(`@rdrive/data-loader: append reloads are JSON-only; table "${name}" is ${entry.format}`),
      );
    }
    const previous = memo.get(name);
    return track(name, async () => {
      // never drop or append to a table another call is still filling
      if (previous) await previous;
      return loadEntry(entry, { ...readParams(), ...extra }, { append, replace: !append });
    });
  };

  // markRaw, like a filter context: a store may hold the loader in reactive state without Vue
  // unwrapping `dataLoaded` (a ref) on the way out.
  return markRaw({ load, ensure, reload, progress, dataLoaded });
}
